import { ContextRegistry } from '../../../core/src/services/ContextRegistry';
import { AIAction } from '../types/module-registry';

/**
 * Reject paramNames that would otherwise resolve through the JavaScript
 * prototype chain. `moduleContext['toString']` returns the inherited
 * `Object.prototype.toString` function even on a fresh `{}`, and
 * `contextDefaults['constructor']` returns the Object constructor — neither
 * is a real user-supplied value, but both leak by default through any
 * dynamic-string property access. `paramName in Object.prototype` covers
 * `__proto__`, `constructor`, `hasOwnProperty`, `toString`, `valueOf`,
 * etc. uniformly.
 */
function _isUnsafeParamName(paramName: string): boolean {
	return typeof paramName !== 'string' || paramName in Object.prototype;
}

/**
 * Resolves special context values like @current and @last
 * @param value The parameter value (could be @current, @last, or a regular value)
 * @param paramName The name of the parameter being resolved
 * @param action The AI action being executed
 * @param moduleContext The current module context from contextProvider
 * @returns The resolved value or undefined if it couldn't be resolved
 */
export function resolveContextValue(
	value: any,
	paramName: string,
	action: AIAction,
	moduleContext?: Record<string, any>,
): any {
	// Return non-context values as-is
	if (typeof value !== 'string' || !value.startsWith('@')) {
		return value;
	}

	// Defense: reject paramNames that would resolve through Object.prototype.
	if (_isUnsafeParamName(paramName)) {
		return undefined;
	}

	switch (value) {
		case '@current':
			// Look for the parameter name as an OWN property only — do not fall
			// through to inherited Object.prototype methods.
			if (
				moduleContext != null &&
				Object.prototype.hasOwnProperty.call(moduleContext, paramName)
			) {
				const currentValue = moduleContext[paramName];
				if (currentValue !== undefined) {
					return currentValue;
				}
			}
			break;

		case '@last':
			// Guard against null/undefined action — callers occasionally invoke
			// resolution before action is wired up, and a thrown TypeError here
			// unwinds the whole render. Treat missing action as "couldn't resolve".
			if (action == null) {
				return undefined;
			}
			{
				const contextDefaults = action.contextDefaults;
				if (
					contextDefaults != null &&
					Object.prototype.hasOwnProperty.call(contextDefaults, paramName)
				) {
					const contextKey = contextDefaults[paramName];
					if (contextKey) {
						const lastValue = ContextRegistry.get(contextKey);
						if (lastValue !== undefined) {
							return lastValue;
						}
					}
				}
			}
			break;

		default:
	}

	return undefined; // Couldn't resolve
}
